import { useCallback, useContext, useRef, useState, useId } from "react";
import classNames from "classnames";
import UserContext from "../context/user";
import logo from "../images/EssilorLuxotticaLogo.svg";
import Icon from "../components/icon";

const InputField = ({ type, label, name, value, onChange, error, deleteLabel, ...other }) => {
  const ref = useRef();
  const fieldId = useId();
  const [visiblePsw, setVisiblePsw] = useState(false);
  const togglePswVisibility = useCallback(() => setVisiblePsw((value) => !value), []);

  const clearInput = useCallback(
    (e) => {
      const fieldName = e.currentTarget.name?.split("clear_")?.[1];
      onChange({ target: { name: fieldName, value: "" } });
      ref?.current?.focus();
    },
    [onChange]
  );

  return (
    <div
      className={classNames("form__element", {
        "form__element--filled": !!value,
        "form__element--error": error,
      })}
    >
      <label className="form__input-label loading-highlighted" htmlFor={fieldId}>
        {label}
      </label>
      <div className="form__login-box">
        <input
          id={fieldId}
          type={visiblePsw && type === "password" ? "text" : type}
          ref={ref}
          name={name}
          className="form__input loading-highlighted"
          value={value}
          onChange={onChange}
          maxLength={80}
          {...other}
        />
        {!!value && (
          <button
            type="button"
            name={`clear_${name}`}
            className="form__icon-wrapper form__icon-wrapper--delete"
            aria-label={deleteLabel}
            tabIndex="-1"
            onClick={clearInput}
            onTouchStart={clearInput}
          >
            <Icon name="delete" className="form__icon-delete" />
          </button>
        )}
        {error && (
          <span className="form__icon-wrapper form__icon-wrapper--error">
            <Icon name="error-sign" />
          </span>
        )}
        {type === "password" && (
          <button
            type="button"
            className="form__icon-wrapper form__icon-wrapper--visibility loading-highlighted"
            aria-label="Toggle password visibility"
            tabIndex="-1"
            onClick={togglePswVisibility}
          >
            <Icon name={visiblePsw ? "eye" : "eye-slash"} />
          </button>
        )}
      </div>
      {error && <div className="form__error-msg">{error}</div>}
    </div>
  );
};

const AppsLogin = ({ app = "SURVEY", setForgotPw, labels }) => {
  const { login } = useContext(UserContext);
  const [{ username, password }, setLoginData] = useState({ username: "", password: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const onChange = useCallback((e) => {
    setLoginData((value) => ({
      ...value,
      [e.target.name]: e.target.value,
    }));
    setErrors({});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username?.length || !password?.length) {
      const emptyFieldError = labels?.NOT_EMPTY_FIELD;
      setErrors({
        username: !username?.length ? emptyFieldError : null,
        password: !password?.length ? emptyFieldError : null,
      });
      return;
    }
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      console.error(err);
      setErrors({
        username: labels?.ACCOUNT_NOT_FOUND,
        password: labels?.ACCOUNT_NOT_FOUND,
      });
    }
    setLoading(false);
  };

  return (
    <div className={classNames("login", Object.keys(labels).length === 0 && "loading-skeleton")}>
      <div className="wrapper">
        <div className="login__box" role="region" aria-label="login">
          {app !== "SURVEY" && (
            <h1 className="login__title">{labels?.[`WELFARE_LOGIN_TITLE_${app}`]}</h1>
          )}
          <img
            className="login__logo"
            src={logo}
            alt="EssilorLuxottica"
            role="heading"
            aria-level="1"
          />
          <form className="form" onSubmit={onSubmit}>
            <InputField
              type="text"
              label={labels?.USERNAME}
              name="username"
              placeholder={`${labels?.USERNAME || ""}`}
              value={username}
              onChange={onChange}
              error={errors.username}
              deleteLabel="Delete username"
              autoComplete="username"
            />
            <InputField
              type="password"
              label={labels?.PASSWORD}
              name="password"
              placeholder={`${labels?.PASSWORD || ""}`}
              value={password}
              onChange={onChange}
              error={errors.password}
              deleteLabel="Delete password"
              autoComplete="current-password"
            />
            <button
              onClick={() => setForgotPw(true)}
              className="login__link-wrapper link loading-highlighted"
              type="button"
            >
              {labels?.LOGIN_AND_PASSWORD_SUPPORT}
            </button>
            <div className="login__submit-wrapper">
              <button
                className={classNames("button primary", { active: loading })}
                disabled={loading || Object.keys(labels).length === 0}
              >
                {
                  <>
                    {loading ? <span className="loading-spinner" /> : null}
                    <span>{labels?.LOGIN}</span>
                  </>
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppsLogin;
